import { SUPPORTED_LOCALES } from '../consts/supported-locales'

/**
 * Identificativo mercato/paese della build (`REACT_APP_MARKET`).
 * Mercati supportati: `fm` (Micronesia), `tj` (Tagikistan). Default `tj`.
 */
export const MARKET_ID = process.env.REACT_APP_MARKET || 'tj'

/**
 * Se true, alla prima visita (senza `preferredLanguage` in localStorage) non si usa la lingua del browser:
 * si attiva subito la lingua del mercato. Utile per white-label con lingua fissa.
 */
export const FORCE_MARKET_LOCALE =
  process.env.REACT_APP_FORCE_MARKET_LOCALE === 'true' ||
  process.env.REACT_APP_FORCE_MARKET_LOCALE === '1'

/**
 * URL pubblico per file sotto `public/` (funziona con homepage non-root se `PUBLIC_URL` è impostato).
 * @param {string} path - Percorso assoluto tipo `/assets/...`
 * @returns {string}
 */
export function getPublicAssetUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`
  const base = process.env.PUBLIC_URL || ''
  return `${base}${normalized}`
}

/**
 * Logo FRED nel footer "Powered by" del login.
 * Usare sempre questo URL (mai `../assets/...`: in produzione con React Router il path relativo è sbagliato).
 */
export const LOGO_FRED_POWERED_BY_URL = getPublicAssetUrl(
  '/assets/logo_fred_senza_payoff_2.png',
)

/**
 * @typedef {Object} MarketBranding
 * @property {string} loginPrimary - Logo principale schermata login (chiaro)
 * @property {string} loginFlag - Bandiera / emblema sinistro login
 * @property {string} loginSeal - Sigillo / logo istituzionale destro login
 * @property {string} poweredByFred - Logo "Powered by" footer login
 * @property {string} toolbarWhite - Logo bianco toolbar desktop/mobile
 * @property {string} authLogoLight - Logo su sfondo chiaro (forgot/signup)
 * @property {string} authLogoDark - Logo su sfondo scuro (forgot/signup)
 */

/**
 * @typedef {Object} MarketGeo
 * @property {[number, number]} mapCenter - Centro mappa iniziale [lon, lat] (WGS84)
 * @property {OfflineArea[]} offlineAreas - Aree prefetch tile offline (una per zona abitata)
 */

/**
 * @typedef {Object} OfflineArea
 * @property {string} name
 * @property {[number, number, number, number]} bbox - [minLon, minLat, maxLon, maxLat] (WGS84)
 */

/**
 * @typedef {Object} MarketDefinition
 * @property {string} buildDefaultLocale
 * @property {string} documentTitle
 * @property {MarketBranding} branding
 * @property {MarketGeo} geo
 * @property {string} ceToolPath - Path del Community Engagement Tool sullo stesso dominio (pagina Help)
 */

/** @type {Record<string, MarketDefinition>} */
const MARKET_REGISTRY = {
  /** Micronesia: francese di default; bandiera e sigillo istituzionali. */
  fm: {
    buildDefaultLocale: 'fr',
    documentTitle: 'ADaMS',
    branding: {
      loginPrimary: '/assets/brands/fm/login_primary.png',
      loginFlag: '/assets/Micronesia_flag.svg',
      loginSeal: '/assets/Seal_Micronesia.svg',
      poweredByFred: '/assets/logo_fred_senza_payoff_2.png',
      toolbarWhite: '/assets/brands/fm/toolbar_white.png',
      authLogoLight: '/assets/brands/fm/auth_logo_light.png',
      authLogoDark: '/assets/brands/fm/auth_logo_dark.png',
    },
    /**
     * Centro su Pohnpei (Palikir, capitale). Un'area offline per l'isola principale di ciascuno dei
     * quattro stati federati: sono a migliaia di km, un bbox unico scaricherebbe soprattutto oceano.
     */
    geo: {
      mapCenter: [158.215, 6.887],
      offlineAreas: [
        { name: 'Yap', bbox: [138.05, 9.43, 138.25, 9.65] },
        { name: 'Chuuk', bbox: [151.5, 7.1, 152.1, 7.75] },
        { name: 'Pohnpei', bbox: [158.05, 6.75, 158.4, 7.05] },
        { name: 'Kosrae', bbox: [162.88, 5.25, 163.05, 5.4] },
      ],
    },
    ceToolPath: '/ce',
  },
  /** Tagikistan: tagiko di default; `Flag_of_Tajikistan` e `GOV_tajikistan`. */
  tj: {
    buildDefaultLocale: 'tg',
    documentTitle: 'ADaMS',
    branding: {
      loginPrimary: '/assets/brands/tj/login_primary.png',
      loginFlag: '/assets/Flag_of_Tajikistan.png',
      loginSeal: '/assets/GOV_tajikistan.png',
      poweredByFred: '/assets/logo_fred_senza_payoff_2.png',
      toolbarWhite: '/assets/brands/tj/toolbar_white.png',
      authLogoLight: '/assets/brands/tj/auth_logo_light.png',
      authLogoDark: '/assets/brands/tj/auth_logo_dark.png',
    },
    /** Dushanbe; area offline storica (Dushanbe → Khujand). */
    geo: {
      mapCenter: [68.787, 38.5598],
      offlineAreas: [
        { name: 'Dushanbe - Khujand', bbox: [68.5, 38.5, 71.5, 41.0] },
      ],
    },
    ceToolPath: '/ce',
  },
}

const FALLBACK_MARKET = MARKET_REGISTRY.tj

/**
 * Lingua di default della build (fallback se browser non supportato / mercato forzato).
 * Deve essere in `SUPPORTED_LOCALES` e avere catalogo Lingui caricato in `index.js`.
 * @returns {string}
 */
export function getBuildDefaultLocale() {
  const fromEnv = process.env.REACT_APP_DEFAULT_LOCALE
  const raw =
    (fromEnv && SUPPORTED_LOCALES.includes(fromEnv)
      ? fromEnv
      : null) ??
    MARKET_REGISTRY[MARKET_ID]?.buildDefaultLocale ??
    FALLBACK_MARKET.buildDefaultLocale

  if (!SUPPORTED_LOCALES.includes(raw)) {
    return SUPPORTED_LOCALES.includes('en') ? 'en' : SUPPORTED_LOCALES[0]
  }
  return raw
}

/**
 * Definizione mercato risolta (con fallback se `REACT_APP_MARKET` non è in registry).
 * @returns {MarketDefinition}
 */
export function getMarketDefinition() {
  return MARKET_REGISTRY[MARKET_ID] ?? FALLBACK_MARKET
}

/**
 * Path pubblici risolti con `getPublicAssetUrl` per uso nei componenti.
 * @returns {Record<keyof MarketBranding, string>}
 */
export function getBrandingUrls() {
  const { branding } = getMarketDefinition()
  /** @type {Record<string, string>} */
  const out = {}
  for (const key of Object.keys(branding)) {
    out[key] = getPublicAssetUrl(branding[key])
  }
  return out
}

/**
 * Titolo documento per il mercato corrente.
 * @returns {string}
 */
export function getMarketDocumentTitle() {
  return getMarketDefinition().documentTitle
}

function envNumber(name) {
  const v = process.env[name]
  if (v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Centro mappa iniziale [lon, lat] del mercato.
 * Override opzionale con `REACT_APP_CENTER_LON` + `REACT_APP_CENTER_LAT` (entrambe valide).
 * @returns {[number, number]}
 */
export function getMapCenter() {
  const lon = envNumber('REACT_APP_CENTER_LON')
  const lat = envNumber('REACT_APP_CENTER_LAT')
  if (lon !== null && lat !== null) return [lon, lat]
  return [...getMarketDefinition().geo.mapCenter]
}

/**
 * Aree prefetch tile offline del mercato.
 * Override con un'unica area se sono valorizzate tutte e quattro
 * `REACT_APP_OFFLINE_MIN_LON` / `MIN_LAT` / `MAX_LON` / `MAX_LAT`.
 * @returns {OfflineArea[]}
 */
export function getOfflineMapAreas() {
  const custom = [
    envNumber('REACT_APP_OFFLINE_MIN_LON'),
    envNumber('REACT_APP_OFFLINE_MIN_LAT'),
    envNumber('REACT_APP_OFFLINE_MAX_LON'),
    envNumber('REACT_APP_OFFLINE_MAX_LAT'),
  ]
  if (custom.every((v) => v !== null)) {
    return [{ name: 'custom', bbox: custom }]
  }
  return getMarketDefinition().geo.offlineAreas.map((area) => ({ ...area, bbox: [...area.bbox] }))
}

/**
 * URL del Community Engagement Tool: è servito dallo stesso server dell'app (es. adams-fsm.com/ce).
 * @returns {string}
 */
export function getCeToolUrl() {
  // eslint-disable-next-line no-restricted-globals
  return new URL(getMarketDefinition().ceToolPath, window.location.origin).href
}
